import React, { useMemo, useState } from 'react';
import { apiService } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import {
  isThraceAdmin,
  resolveThraceNation,
  thraceNationsForUser,
  THRACE_SELECT_CLASS,
} from '../../utils/thraceCountry';

type InvRow = {
  area_id: number;
  area_name: string;
  cattle: number;
  sheep: number;
  goat: number;
  buffalo: number;
  pig: number;
};

const SPECIES = ['cattle', 'sheep', 'goat', 'buffalo', 'pig'] as const;

const ThraceInventoryPanel: React.FC = () => {
  const { user } = useAuthStore();
  const nations = useMemo(
    () => thraceNationsForUser(user?.role, user?.country),
    [user?.role, user?.country],
  );
  const admin = isThraceAdmin(user?.role);
  const defaultNation =
    resolveThraceNation(user?.country)?.nationId ?? nations[0]?.nationId ?? 300;
  const [nationId, setNationId] = useState(defaultNation);
  const [year, setYear] = useState(new Date().getFullYear());
  const [areaLabel, setAreaLabel] = useState('District');
  const [rows, setRows] = useState<InvRow[]>([]);
  const [sourceNote, setSourceNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const res = await apiService.thrace.getInventory(nationId, year);
      const data = res.data;
      setAreaLabel(data.area_label || 'District');
      setRows(data.rows || []);
      if (data.prefilled_from_prior && data.source_year) {
        setSourceNote(
          `Numbers shown are copied from ${data.source_year} (no data yet for ${year}). Update and save.`,
        );
      } else if (data.source_year === year) {
        setSourceNote(`Showing saved inventory for ${year}.`);
      } else {
        setSourceNote('No previous numbers found — fill in and save.');
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load inventory');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const setCell = (idx: number, key: (typeof SPECIES)[number], value: string) => {
    const n = value === '' ? 0 : Math.max(0, Math.floor(Number(value) || 0));
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: n } : r)));
  };

  const save = async () => {
    if (!rows.length) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const res = await apiService.thrace.saveInventory({
        nation_id: nationId,
        year,
        rows: rows.map((r) => ({
          area_id: r.area_id,
          cattle: r.cattle,
          sheep: r.sheep,
          goat: r.goat,
          buffalo: r.buffalo,
          pig: r.pig,
        })),
      });
      if (res.data?.success === false) {
        setError((res.data.errors || []).join('\n') || res.data.message);
      } else {
        setMessage(res.data?.message || 'Saved.');
        setSourceNote(`Showing saved inventory for ${year}.`);
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Enter yearly animal population by {areaLabel.toLowerCase()}. Changes save directly to the
        database (no Excel).
      </p>
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
          {admin ? (
            <select
              value={nationId}
              onChange={(e) => setNationId(Number(e.target.value))}
              className={THRACE_SELECT_CLASS}
            >
              {nations.map((n) => (
                <option key={n.nationId} value={n.nationId}>
                  {n.label}
                </option>
              ))}
            </select>
          ) : (
            <div className={`${THRACE_SELECT_CLASS} bg-gray-50 text-gray-800`}>
              {nations.find((n) => n.nationId === nationId)?.label || '—'}
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
          <input
            type="number"
            min={2016}
            max={2100}
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="w-28 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
        <button className="nav-btn" onClick={load} disabled={loading || saving}>
          {loading ? 'Loading…' : 'Load'}
        </button>
        <button
          className="nav-btn"
          onClick={save}
          disabled={saving || loading || !rows.length}
        >
          {saving ? 'Saving…' : 'Save inventory'}
        </button>
      </div>

      {sourceNote && <p className="text-xs text-gray-500">{sourceNote}</p>}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm whitespace-pre-wrap">
          {error}
        </div>
      )}
      {message && (
        <div className="p-3 bg-green-50 border border-green-200 text-green-800 rounded text-sm">
          {message}
        </div>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-gray-700">{areaLabel}</th>
                {SPECIES.map((s) => (
                  <th key={s} className="text-right px-3 py-2 font-medium text-gray-700 capitalize">
                    {s}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.area_id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-1.5 text-gray-800 whitespace-nowrap">{r.area_name}</td>
                  {SPECIES.map((s) => (
                    <td key={s} className="px-2 py-1">
                      <input
                        type="number"
                        min={0}
                        value={r[s]}
                        onChange={(e) => setCell(idx, s, e.target.value)}
                        className="w-24 text-right px-2 py-1 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ThraceInventoryPanel;
